import React, { useEffect, useRef } from "react"
import * as am5 from "@amcharts/amcharts5"
import * as am5xy from "@amcharts/amcharts5/xy"
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated"

// Utils
import { transformeData } from "../utils/global"
import {
  createLegend,
  clearLegends,
  configureNumberFormatting,
  createExportMenu,
} from "../utils/utils"

// Types
interface BubbleChartProps {
  idChart?: string
  widgetTitle?: string
  height?: string
  width?: string
  dataChart?: any[]
  dataNames?: string
  showMenu?: boolean
  enableExportMenu?: boolean
  defaultColor?: string
  // DATA
  categoryXId?: string
  categoryYId?: string
  categoryValueId?: string
  categoryNameId?: string
  // LEGEND
  visibilityLegend?: boolean
  displayTypeLegendLabels?: "truncate" | "wrap" | "default"
  markerLegendHeigth?: number
  markerLegendWidth?: number
  legendPosition?: "top" | "bottom" | "left" | "right"
  fontSizeLegend?: number
  // AXIS
  titleAxeX?: string
  titleAxeY?: string
  hideAxeXLabel?: boolean
  hideAxeYLabel?: boolean
  useMinMaxYAxis?: boolean
  minYAxis?: number
  maxYAxis?: number
  axisStartZero?: boolean
  // VALUE
  numberDecimal?: number
  decimalSeparator?: string
  showSpaceForThousand?: boolean
  // GRID
  disabledGridAxesX?: boolean
  disabledGridAxesY?: boolean
  requestLoad?: any
  fieldsData?: any
}

const BubbleChart: React.FC<BubbleChartProps> = ({
  idChart,
  widgetTitle = "BubbleChart",
  height = "100%",
  width = "100%",
  dataChart = [],
  dataNames = "",
  showMenu = true,
  enableExportMenu = false,
  defaultColor = "#2A0B81",
  categoryXId = "x",
  categoryYId = "y",
  categoryValueId = "value",
  categoryNameId = "name",
  visibilityLegend = false,
  displayTypeLegendLabels = "truncate",
  markerLegendHeigth = 20,
  markerLegendWidth = 20,
  legendPosition = "right",
  fontSizeLegend = 15,
  titleAxeX = "Titre de l'axe X",
  titleAxeY = "Titre de l'axe Y",
  hideAxeXLabel = false,
  hideAxeYLabel = false,
  useMinMaxYAxis = false,
  minYAxis = 0,
  maxYAxis = 1000,
  axisStartZero = false,
  numberDecimal = 2,
  decimalSeparator = ",",
  showSpaceForThousand = true,
  disabledGridAxesX = false,
  disabledGridAxesY = false,
  requestLoad,
  fieldsData,
}) => {
  const ID = idChart || `bubleChart${Math.random()}`
  const chartRef = useRef<am5xy.XYChart | null>(null)
  const rootRef = useRef<am5.Root | null>(null)

  const dataTransformed = transformeData(requestLoad, fieldsData, dataChart)

  const initChart = () => {
    const root = am5.Root.new(ID)
    root._logo?.dispose()
    rootRef.current = root

    root.setThemes([am5themes_Animated.new(root)])

    // Number formatting
    configureNumberFormatting(root, {
      decimalSeparator,
      numberDecimal,
      showSpaceForThousand,
    })

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelX: "panX",
        wheelY: "zoomX",
        layout: root.verticalLayout,
      })
    )

    chartRef.current = chart

    // Export menu
    createExportMenu(root, {
      enableExportMenu: enableExportMenu ?? false,
      filePrefix: widgetTitle,
      dataSource: dataTransformed,
    })

    // Cursor
    chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        behavior: "zoomXY",
      })
    )

    // Scrollbars
    chart.set("scrollbarX", am5.Scrollbar.new(root, { orientation: "horizontal" }))
    chart.set("scrollbarY", am5.Scrollbar.new(root, { orientation: "vertical" }))

    // Legend - use root.container to avoid layout issues
    createLegend(root.container, root, {
      visibilityLegend,
      legendPosition,
      displayTypeLegendLabels,
      fontSizeLegend,
      markerLegendWidth,
      markerLegendHeight: markerLegendHeigth,
    })
  }

  const updateChart = (chart: am5xy.XYChart, root: am5.Root) => {
    // Always rebuild axes and series (matching original JS behavior)
    // Clear existing axes and series
    chart.xAxes.clear()
    chart.yAxes.clear()
    chart.series.clear()

    // X-axis (Value)
    const xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererX.new(root, {}),
      })
    )

    xAxis.children.push(
      am5.Label.new(root, {
        text: titleAxeX,
        x: am5.p50,
        centerX: am5.p50,
      })
    )

    xAxis.get("renderer").labels.template.set("visible", !hideAxeXLabel)
    xAxis.get("renderer").grid.template.set("visible", !disabledGridAxesX)

    // Y-axis (Value)
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
      })
    )

    yAxis.children.unshift(
      am5.Label.new(root, {
        text: titleAxeY,
        rotation: -90,
        y: am5.p50,
        centerX: am5.p50,
      })
    )

    yAxis.get("renderer").labels.template.set("visible", !hideAxeYLabel)
    yAxis.get("renderer").grid.template.set("visible", !disabledGridAxesY)

    if (useMinMaxYAxis && minYAxis !== undefined && maxYAxis !== undefined) {
      yAxis.set("min", minYAxis)
      yAxis.set("max", maxYAxis)
      yAxis.set("strictMinMax", true)
    } else if (axisStartZero) {
      yAxis.set("min", 0)
    }

    // Bubble series
    const series = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: dataNames,
        calculateAggregates: true,
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: categoryYId,
        valueXField: categoryXId,
        valueField: categoryValueId,
        stroke: am5.color(defaultColor),
        fill: am5.color(defaultColor),
        tooltip: am5.Tooltip.new(root, {
          labelText: "{name} : {value}",
        }),
      })
    )

    // Hide the line stroke
    series.strokes.template.set("strokeOpacity", 0)

    // Configure bullet template - create it BEFORE the factory
    const circleTemplate = am5.Template.new<am5.Circle>({})

    // Add bullets using the template
    series.bullets.push(function (root) {
      const graphics = am5.Circle.new(
        root,
        {
          fill: series.get("fill"),
        },
        circleTemplate
      )

      // Hover state
      graphics.states.create("hover", {
        fillOpacity: 1,
        strokeOpacity: 1,
      })

      // Tooltip position adapter
      graphics.adapters.add(
        "tooltipY",
        (tooltipY: number | am5.Percent | undefined, target: any) => {
          const circle = target as am5.Circle
          return -(circle.get("radius") || 0)
        }
      )

      return am5.Bullet.new(root, {
        sprite: graphics,
      })
    })

    // Heat rules for bubble size based on value
    series.set("heatRules", [
      {
        target: circleTemplate,
        min: 3,
        max: 40,
        dataField: "value",
        key: "radius",
      },
    ])

    series.data.setAll(dataChart)
  }

  useEffect(() => {
    initChart()

    return () => {
      if (rootRef.current) {
        rootRef.current.dispose()
      }
    }
  }, [])

  useEffect(() => {
    if (chartRef.current && rootRef.current) {
      const chart = chartRef.current
      const root = rootRef.current

      // Clear legends before update
      clearLegends(root.container)

      // Recreate export menu with latest data
      createExportMenu(root, {
        enableExportMenu: enableExportMenu ?? false,
        filePrefix: widgetTitle,
        dataSource: dataTransformed,
      })

      // Recreate legend
      const legend = createLegend(root.container, root, {
        visibilityLegend,
        legendPosition,
        displayTypeLegendLabels,
        fontSizeLegend,
        markerLegendWidth,
        markerLegendHeight: markerLegendHeigth,
      })

      updateChart(chart, root)

      // Populate legend with series data
      if (legend) {
        const seriesArray: am5.Series[] = []
        chart.series.each(series => {
          seriesArray.push(series)
        })
        legend.data.setAll(seriesArray)
      }
    }
  })

  return <div id={ID} style={{ width, height }} />
}

export default BubbleChart
